import { useMemo, useState } from 'react'
import { useAuthStore, useWatchlistStore, usePlayerStore } from '../stores'
import { useTrialStore } from '../stores/useTrialStore'
import { Bookmark, User, MapPin, Star, Trash2, Send, Calendar, Clock, Phone, FileText, X, CheckCircle2, Loader2 } from 'lucide-react'

interface SavedPlayersProps {
  onSelectPlayer?: (playerId: string) => void
}

function SavedPlayers({ onSelectPlayer }: SavedPlayersProps) {
  const { user } = useAuthStore()
  const { watchlist, removeFromWatchlist } = useWatchlistStore()
  const { players } = usePlayerStore()
  const { sendInvitation } = useTrialStore()
  const [invitePlayerId, setInvitePlayerId] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [success, setSuccess] = useState('')
  const [form, setForm] = useState({
    venue: '',
    trial_date: '',
    duration: '',
    contact_info: '',
    notes: '',
  })

  const isClub = user?.role === 'club'

  const savedPlayers = useMemo(() => {
    return watchlist
      .map(id => players.find(p => p.id === id))
      .filter(Boolean)
  }, [watchlist, players])

  const invitePlayer = useMemo(() => {
    if (!invitePlayerId) return null
    return players.find(p => p.id === invitePlayerId) || null
  }, [invitePlayerId, players])

  const resetForm = () => {
    setForm({ venue: '', trial_date: '', duration: '', contact_info: '', notes: '' })
    setInvitePlayerId(null)
  }

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!invitePlayer || !user || !form.venue || !form.trial_date) return

    setSending(true)
    try {
      await sendInvitation({
        player_id: invitePlayer.id,
        club_id: user.id,
        club_name: user.name || 'Club',
        venue: form.venue,
        trial_date: form.trial_date,
        duration: form.duration || undefined,
        contact_info: form.contact_info || undefined,
        notes: form.notes || undefined,
      })
      resetForm()
      setSuccess(`Trial invitation sent to ${invitePlayer.name}!`)
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      console.error('Failed to send invitation:', err)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Bookmark size={24} className="text-[#7cff01]" />
        <div>
          <h2 className="text-xl font-black text-white">Saved Players</h2>
          <p className="text-xs text-gray-500 uppercase tracking-widest">
            {savedPlayers.length} player{savedPlayers.length !== 1 ? 's' : ''} saved
          </p>
        </div>
      </div>

      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-900/20 border border-green-700/30 rounded-xl text-green-400 text-xs font-bold">
          <CheckCircle2 size={16} />
          {success}
        </div>
      )}

      {savedPlayers.length === 0 ? (
        <div className="text-center py-20 bg-white/2 rounded-3xl border border-white/5">
          <Bookmark className="mx-auto text-gray-600 mb-4" size={48} />
          <h3 className="text-lg font-bold text-white">No Saved Players</h3>
          <p className="text-gray-500 mt-2 text-sm">
            Browse the database and save players to track them here
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {savedPlayers.map((player) => player && (
            <div
              key={player.id}
              className="bg-white/2 border border-white/5 rounded-3xl p-6 hover:bg-white/5 transition cursor-pointer group"
              onClick={() => onSelectPlayer?.(player.id)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-[#7cff01]/10 border border-[#7cff01]/20 flex items-center justify-center overflow-hidden">
                    {(player as any).avatar_url ? (
                      <img src={(player as any).avatar_url} alt={player.name} className="w-full h-full object-cover" />
                    ) : (
                      <User size={20} className="text-[#7cff01]" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-white truncate">{player.name}</h3>
                    <p className="text-[10px] text-gray-500 uppercase tracking-widest">{player.main_position || 'Player'}</p>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    removeFromWatchlist(player.id)
                  }}
                  className="p-2 text-gray-600 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition opacity-0 group-hover:opacity-100"
                  title="Remove from saved"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              <div className="space-y-2">
                {player.club && (
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <span className="text-gray-600">Club:</span>
                    <span className="font-bold">{player.club}</span>
                  </div>
                )}
                {player.age && (
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <span className="text-gray-600">Age:</span>
                    <span className="font-bold">{player.age}</span>
                  </div>
                )}
                {player.state && (
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <MapPin size={12} className="text-gray-600" />
                    <span className="font-bold">{player.state}</span>
                  </div>
                )}
              </div>

              {(player as any).is_top_talent && (
                <div className="mt-3 flex items-center gap-1.5 text-[10px] font-black text-[#ffb703] uppercase tracking-widest">
                  <Star size={12} />
                  Top Talent
                </div>
              )}

              {isClub && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setInvitePlayerId(player.id)
                  }}
                  className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-500/10 border border-green-500/30 rounded-xl text-green-400 hover:bg-green-500/20 transition text-xs font-bold uppercase tracking-widest"
                >
                  <Send size={12} />
                  Send Trial Invitation
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Trial Invitation Modal */}
      {invitePlayer && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-white">Trial Invitation</h3>
                <p className="text-xs text-gray-500 mt-1">Send to {invitePlayer.name}</p>
              </div>
              <button onClick={resetForm} className="p-2 text-gray-500 hover:text-white rounded-xl hover:bg-white/5 transition">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSendInvite} className="space-y-4">
              <div className="space-y-1">
                <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 uppercase tracking-widest">
                  <Calendar size={12} />
                  Trial Date *
                </label>
                <input
                  type="datetime-local"
                  value={form.trial_date}
                  onChange={(e) => setForm(prev => ({ ...prev, trial_date: e.target.value }))}
                  required
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
                />
              </div>

              <div className="space-y-1">
                <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 uppercase tracking-widest">
                  <MapPin size={12} />
                  Venue / Address *
                </label>
                <input
                  type="text"
                  value={form.venue}
                  onChange={(e) => setForm(prev => ({ ...prev, venue: e.target.value }))}
                  placeholder="e.g. National Stadium, Surulere, Lagos"
                  required
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
                />
              </div>

              <div className="space-y-1">
                <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 uppercase tracking-widest">
                  <Clock size={12} />
                  Duration
                </label>
                <input
                  type="text"
                  value={form.duration}
                  onChange={(e) => setForm(prev => ({ ...prev, duration: e.target.value }))}
                  placeholder="e.g. 2 weeks, 1 day, Ongoing"
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
                />
              </div>

              <div className="space-y-1">
                <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 uppercase tracking-widest">
                  <Phone size={12} />
                  Contact Info
                </label>
                <input
                  type="text"
                  value={form.contact_info}
                  onChange={(e) => setForm(prev => ({ ...prev, contact_info: e.target.value }))}
                  placeholder="e.g. +234 801 234 5678, coach@club.com"
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
                />
              </div>

              <div className="space-y-1">
                <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 uppercase tracking-widest">
                  <FileText size={12} />
                  Notes
                </label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional details about the trial..."
                  rows={3}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold resize-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-gray-400 hover:text-white transition text-xs font-bold uppercase tracking-widest"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sending || !form.venue || !form.trial_date}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 border border-green-500 rounded-xl text-black hover:bg-green-400 transition text-xs font-bold uppercase tracking-widest disabled:opacity-50"
                >
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {sending ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default SavedPlayers
